import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {
  isNotNullable,
  Product,
  ProductService,
  RoutingService,
  TranslationService,
} from '@spartacus/core';
import {
  ProductMultiDimensionalSelectorService,
  VariantCategoryGroup,
  VariantCategoryOption,
} from '@spartacus/product-multi-dimensional/selector/core';
import {ActivatedRoute} from '@angular/router';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  take,
  tap,
} from 'rxjs/operators';
import {CurrentProductService} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import { ProductScope } from 'src/app/core/models/gonvarri-product-scope';
import { DEFAULT_KG } from 'src/app/utils/weight-utils';

@Component({
    selector: 'app-gonvarri-product-multi-dimensional-selector',
    templateUrl: './gonvarri-product-multi-dimensional-selector.component.html',
    styleUrl: './gonvarri-product-multi-dimensional-selector.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GonvarriProductMultiDimensionalSelectorComponent implements OnInit {

  disableAddToCart: boolean = false;
  numberOfSheets: number = 10;
  packageWeight: number = DEFAULT_KG;
  largoDeseado: number = 0;
  largoError: string | null = null;
  packProductFixedWeight: number | undefined = undefined;

  isContract: boolean | undefined;

  protected multiDimensionalService: ProductMultiDimensionalSelectorService =
    inject(ProductMultiDimensionalSelectorService);
  protected productService: ProductService = inject(ProductService);
  protected routingService: RoutingService = inject(RoutingService);
  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected currentProductService: CurrentProductService = inject(
    CurrentProductService
  );
  protected translationService: TranslationService = inject(TranslationService);


  constructor(public cd: ChangeDetectorRef) {
  }

  selectedProductCode: string = "";
  showLargoInput: Boolean = false;
  categories: VariantCategoryGroup[] = [];


  ngOnInit(): void {
    const storedLargoDeseado = localStorage.getItem('largoDeseado');

    this.largoDeseado = storedLargoDeseado ? parseInt(storedLargoDeseado, 10) : 350;
  }

  validateLargo(): void {
    if (this.largoDeseado < 350) {
      this.largoError = 'Valor Mínimo: 350mm';
    } else if (this.largoDeseado > 12000) {
      this.largoError = 'Valor Máximo: 12000mm';
      this.largoDeseado = 12000;
    } else {
      this.largoError = null;
    }
    localStorage.setItem('largoDeseado', this.largoDeseado.toString());
  }

  product$: Observable<any> = this.currentProductService
    .getProduct(ProductScope.DETAILS)
    .pipe(
      filter(isNotNullable),
      filter((product) => !!product.multidimensional),
      distinctUntilChanged(),
      shareReplay(1),
      tap((product) => {
        this.categories = this.getVariants(product);
        this.categories.forEach(category => {
          if(category.name === 'Largo de la chapa' || category.name === 'Comprimento da chapa' || category.name === 'Length') {
            category.variantOptions.forEach(option => {
              if((option.value === "Eligir otro Largo") || (option.value === "Escolher largura") || (option.value === "Choose Lenght")) {
                this.showLargoInput = option.code === product.code;
              }
            });
          }
        });
        this.selectedProductCode = product.code ?? '';
      })
    );

  restrictLength(event: KeyboardEvent): void {
    const input = (event.target as HTMLInputElement).value;

    if (input.length >= 5) {
      event.preventDefault();
    }
  }

  validateShowLargoInput(value: string): void {
    this.showLargoInput = (value === "Eligir otro Largo") || (value === "Escolher largura") || (value === "Choose Lenght");
  }

  changeVariant(code: string | undefined, value: string): void {
    this.validateShowLargoInput(value);
    if (code) {
      this.productService
        .get(code, ProductScope.LIST)
        .pipe(filter(Boolean), take(1))
        .subscribe((product: Product) => {
          this.routingService.go({
            cxRoute: 'product',
            params: product,
          });
          // Reset values when changing product variant to avoid conflicts
          this.updateIsDisabled(false);
          this.updateValues([DEFAULT_KG, 10]);
          this.setPackProductFixedWeight(undefined);
        });
    }

    this.cd.detectChanges();
  }

  getSelectedValue(categoryName: string): string {
    const category = this.categories.find((cat) => cat.name === categoryName);

    if (category && this.selectedProductCode) {
      const selectedOption = category.variantOptions.find(
        (option: VariantCategoryOption) =>
          option.code === this.selectedProductCode
      );

      return selectedOption ? selectedOption.value : '';
    }

    return '';
  }

  onAriaLabel(
    option: VariantCategoryOption,
    categoryName: string
  ): Observable<string> {
    const isSelected = this.isSelected(option.code);

    if (isSelected) {
      return this.translationService
        .translate('multiDimensionalSelector.selectedVariant')
        .pipe(map((text) => `${text}, ${option.value} ${categoryName}`));
    }

    return this.translationService
      .translate('multiDimensionalSelector.variantThumbnailTitle', {
        value: option.value,
        category: categoryName,
      })
      .pipe(map((text) => `${text}`));
  }

  passThrought(value: any): any {
    return value;
  }

  getCategoryName(category: VariantCategoryGroup): string {
    const label = `${category.name}: `;
    if (category.hasImages) {
      const selectedValue = this.getSelectedValue(category.name);
      return `${label}${selectedValue ?? ''}`;
    }

    return label;
  }

  updateValues(values: number[]): void {
    this.packageWeight = values[0];
    this.numberOfSheets = values[1];
  }

  updateIsDisabled(isDisabled: boolean): void {
    this.disableAddToCart = isDisabled;
  }

  setPackProductFixedWeight(value: number | undefined): void {
    this.packProductFixedWeight = value;
  }

  protected isSelected(code: string): boolean {
    return code === this.selectedProductCode;
  }

  protected getVariants(product: Product) {
    return this.multiDimensionalService.getVariants(product);
  }
}
